// Stock documents — adjustments and transfers.
//
//   list      stock_adjustments?select=…,reason,company&order=created_at.desc,id.desc&limit=51
//             keyset on (created_at, id): index stock_adjustments_list_idx /
//             stock_adjustments_status_idx (with a status filter). Same for transfers.
//   detail    rpc stock_document(type, id) — header + lines with codes, one round-trip.
//   writes    rpc save_* / post_* / cancel_* / reverse_* / post_quick_transfer;
//             the tables are read-only for API users. Posting calls the ledger
//             engine once; numbers are allocated at posting (gapless).

import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { Page } from "@/components/DataList";
import { isDemoMode, supabase } from "@/lib/supabase";
import { demoApi } from "./demo";
import type { ListFilter, ListSort } from "@patronix/domain/list";
import { applyRestFilters, applyRestSort, restCursorOf, type RestColumn, type RestCursor } from "@/lib/restList";

export type DocumentStatus = "draft" | "posted" | "cancelled";
export type DocumentType = "stock_adjustment" | "stock_transfer";
export type StatusFilter = "all" | DocumentStatus;

export interface AdjustmentReason {
  code: string;
  description: string;
  direction: "in" | "out" | "both";
  is_active: boolean;
}

export interface DocumentRow {
  id: string;
  number: string | null;
  status: DocumentStatus;
  note: string | null;
  created_at: string;
  posted_at: string | null;
  company: { code: string } | null;
  /** Adjustments only. */
  reason_code?: string;
  reason?: { description: string } | null;
  reverses_adjustment_id?: string | null;
}

export interface AdjustmentLine {
  id: string;
  line_no: number;
  item_id: string;
  item_code: string;
  item_description: string;
  lot_controlled: boolean;
  warehouse_id: string;
  warehouse_code: string;
  location_id: string;
  location_code: string;
  lot_id: string | null;
  lot_code: string | null;
  expiry_date: string | null;
  qty: number | string;
  uom_id: string;
  uom_code: string;
  uom_decimals: number;
  transaction_id: number | null;
  project_id?: string | null;
  project_code?: string | null;
}

export interface TransferLine {
  id: string;
  line_no: number;
  item_id: string;
  item_code: string;
  item_description: string;
  lot_controlled: boolean;
  from_warehouse_id: string;
  from_warehouse_code: string;
  from_location_id: string;
  from_location_code: string;
  to_warehouse_id: string;
  to_warehouse_code: string;
  to_location_id: string;
  to_location_code: string;
  lot_id: string | null;
  lot_code: string | null;
  qty: number | string;
  uom_id: string;
  uom_code: string;
  uom_decimals: number;
  from_project_id?: string | null;
  from_project_code?: string | null;
  to_project_id?: string | null;
  to_project_code?: string | null;
}

interface DocumentBase {
  id: string;
  number: string | null;
  status: DocumentStatus;
  company_id: string;
  company_code: string;
  note: string | null;
  created_at: string;
  posted_at: string | null;
  updated_at: string;
}

export interface AdjustmentDocument extends DocumentBase {
  type: "stock_adjustment";
  reason_code: string;
  reason_description: string;
  reason_direction: AdjustmentReason["direction"];
  reverses_adjustment_id: string | null;
  reverses_number: string | null;
  reversed_by: { id: string; number: string } | null;
  /** All lines of the document; `lines` holds at most the first 1 000 (imported opening stock can have up to 10 000). */
  line_count: number;
  lines: AdjustmentLine[];
}

export interface TransferDocument extends DocumentBase {
  type: "stock_transfer";
  lines: TransferLine[];
}

/** What the editor sends; the database fills defaults (DEFAULT location, base unit). */
export interface AdjustmentLineInput {
  item_id: string;
  warehouse_id: string;
  location_id?: string | undefined;
  lot_id?: string | undefined;
  lot_code?: string | undefined;
  expiry_date?: string | undefined;
  qty: string;
  uom_id?: string | undefined;
  /** Project stock to correct (PR 3a); absent = free stock. */
  project_id?: string | undefined;
}

export interface TransferLineInput {
  item_id: string;
  from_warehouse_id: string;
  from_location_id?: string | undefined;
  to_warehouse_id: string;
  to_location_id?: string | undefined;
  lot_id?: string | undefined;
  qty: string;
  uom_id?: string | undefined;
  /** Source / target project stock (R4); absent = free stock. */
  from_project_id?: string | undefined;
  to_project_id?: string | undefined;
}

export interface PostResult {
  id: string;
  number?: string;
  status: DocumentStatus;
}

export const DOC_PAGE = 50;

function db() {
  if (!supabase) throw new Error("Supabase is not configured");
  return supabase;
}

const tableOf = { stock_adjustment: "stock_adjustments", stock_transfer: "stock_transfers" } as const;
const listColumns = {
  stock_adjustment:
    "id,number,status,note,created_at,posted_at,reason_code,reverses_adjustment_id," +
    "company:companies!stock_adjustments_company_fkey(code),reason:adjustment_reasons!stock_adjustments_reason_fkey(description)",
  stock_transfer: "id,number,status,note,created_at,posted_at,company:companies!stock_transfers_company_fkey(code)",
} as const;

// Column filters and sort (stockAdjustmentListColumns / stockTransferListColumns)
// on the table's own columns; default newest first. Indexes: the documents'
// (tenant_id, created_at, id) list index serves the default order; other
// sorts and text filters run over the tenant's documents (RLS).
const restColumns: RestColumn[] = [
  { key: "number", column: "number", type: "text", nullable: true },
  { key: "date", column: "created_at", type: "date", timestamp: true },
  { key: "reason", column: "reason_code", type: "enum" },
  { key: "note", column: "note", type: "text", nullable: true },
  { key: "status", column: "status", type: "enum" },
];
const defaultSort: ListSort = { col: "date", dir: "desc" };

export interface DocumentListView {
  filters?: ListFilter[];
  sort?: ListSort | null;
}

export async function listDocuments(
  type: DocumentType,
  status: StatusFilter,
  cursor: string | null,
  signal: AbortSignal,
  view: DocumentListView = {},
  size = DOC_PAGE,
): Promise<Page<DocumentRow>> {
  if (isDemoMode) return demoApi.list(type, status, cursor);
  let query = db().from(tableOf[type]).select(listColumns[type]);
  if (status !== "all") query = query.eq("status", status);
  query = applyRestFilters(query, restColumns, view.filters ?? []);
  query = applyRestSort(query, restColumns, view.sort ?? null, defaultSort, cursor ? (JSON.parse(cursor) as RestCursor) : null);
  const { data, error } = await query.limit(size + 1).abortSignal(signal);
  if (error) throw error;
  const rows = data as unknown as DocumentRow[];
  const hasMore = rows.length > size;
  const page = hasMore ? rows.slice(0, size) : rows;
  const last = page[page.length - 1];
  return {
    rows: page,
    nextCursor: hasMore && last ? restCursorOf(last as unknown as Record<string, unknown>, restColumns, view.sort ?? null, defaultSort) : null,
  };
}

async function getDocument(type: DocumentType, id: string, signal: AbortSignal) {
  if (isDemoMode) return demoApi.get(type, id);
  const { data, error } = await db().rpc("stock_document", { p_type: type, p_id: id }).abortSignal(signal);
  if (error) throw error;
  return data as AdjustmentDocument | TransferDocument | null;
}

async function listReasons(signal: AbortSignal): Promise<AdjustmentReason[]> {
  if (isDemoMode) return demoApi.reasons();
  const { data, error } = await db()
    .from("adjustment_reasons")
    .select("code,description,direction,is_active")
    .order("code")
    .abortSignal(signal);
  if (error) throw error;
  return data as AdjustmentReason[];
}

async function rpc<T>(fn: string, args: Record<string, unknown>): Promise<T> {
  const { data, error } = await db().rpc(fn, args);
  if (error) throw error;
  return data as T;
}

export function saveAdjustment(
  id: string | null,
  companyId: string,
  reasonCode: string,
  note: string,
  lines: AdjustmentLineInput[],
): Promise<string> {
  if (isDemoMode) return demoApi.saveAdjustment(id, companyId, reasonCode, note, lines);
  return rpc<string>("save_stock_adjustment", {
    p_id: id,
    p_company_id: companyId,
    p_reason_code: reasonCode,
    p_note: note,
    p_lines: lines,
  });
}

export function postDocument(type: DocumentType, id: string): Promise<PostResult> {
  if (isDemoMode) return demoApi.post(type, id);
  return rpc<PostResult>(type === "stock_adjustment" ? "post_stock_adjustment" : "post_stock_transfer", { p_id: id });
}

export function cancelDocument(type: DocumentType, id: string): Promise<PostResult> {
  if (isDemoMode) return demoApi.cancel(type, id);
  return rpc<PostResult>(type === "stock_adjustment" ? "cancel_stock_adjustment" : "cancel_stock_transfer", { p_id: id });
}

export function reverseAdjustment(id: string): Promise<PostResult> {
  if (isDemoMode) return demoApi.reverse(id);
  return rpc<PostResult>("reverse_stock_adjustment", { p_id: id });
}

export function postQuickTransfer(companyId: string, lines: TransferLineInput[], note: string): Promise<PostResult> {
  if (isDemoMode) return demoApi.quickTransfer(companyId, lines, note);
  return rpc<PostResult>("post_quick_transfer", { p_company_id: companyId, p_lines: lines, p_note: note });
}

// ---------------------------------------------------------------------------
// Query options
// ---------------------------------------------------------------------------

export const documentKeys = {
  all: ["warehousing", "documents"] as const,
  list: (type: DocumentType, status: StatusFilter, view: DocumentListView = {}) => [...documentKeys.all, type, "list", status, view] as const,
  detail: (type: DocumentType, id: string) => [...documentKeys.all, type, "detail", id] as const,
  reasons: () => [...documentKeys.all, "reasons"] as const,
};

export function documentListQuery(type: DocumentType, status: StatusFilter, view: DocumentListView = {}) {
  return infiniteQueryOptions({
    queryKey: documentKeys.list(type, status, view),
    queryFn: ({ pageParam, signal }) => listDocuments(type, status, pageParam, signal, view),
    initialPageParam: null as string | null,
    getNextPageParam: (last: Page<DocumentRow>) => last.nextCursor,
    staleTime: 30_000,
  });
}

export function documentDetailQuery(type: DocumentType, id: string) {
  return queryOptions({
    queryKey: documentKeys.detail(type, id),
    queryFn: ({ signal }) => getDocument(type, id, signal),
    staleTime: 30_000,
  });
}

export const reasonListQuery = queryOptions({
  queryKey: documentKeys.reasons(),
  queryFn: ({ signal }) => listReasons(signal),
  staleTime: 10 * 60_000,
});
